import { refreshAccessToken } from "@stryker/core/api/accounts";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import type { NavigateFunction } from "react-router-dom";
import InfoIcon from "@/assets/icons/info.svg?react";
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, TextInput, Typography } from "@/components/ui";
import type { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { getAccessToken } from "@/utils/storageService";

interface AiResponse {
  response?: string;
  message?: string;
  chatId?: string;
  job?: { jobId: string };
  partialPayload?: Record<string, unknown>;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  attachments?: string[];
}

interface AiChatProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  isFullPage?: boolean;
  aiConfigOptions: Record<string, unknown> | undefined;
  isConfigLoading: boolean;
  userId: string;
  setDefaultValues: (values: Record<string, unknown>) => void;
  toast: ReturnType<typeof useToast>["toast"];
  navigate: NavigateFunction;
  sections: Record<string, unknown>;
}

export default function AiChat({
  isOpen = false,
  onOpenChange,
  onClose,
  isFullPage = false,
  aiConfigOptions,
  isConfigLoading,
  userId,
  setDefaultValues,
  toast,
  navigate,
  sections,
}: AiChatProps) {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatId, setChatId] = useState<string | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<string[]>([]);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);

  const { mutate: sendAiMessage } = useMutation<AiResponse, Error, string>({
    mutationFn: async (userInput: string) => {
      // Prevent send if config is loading
      if (isConfigLoading) {
        throw new Error("Config options are still loading. Please wait.");
      }
      const filteredConfigOptions = aiConfigOptions
        ? {
            diameter_to_thickness: aiConfigOptions.diameter_to_thickness ?? [],
            job_types: aiConfigOptions.job_types ?? [],
            mechanical_calculation_cases: aiConfigOptions.mechanical_calculation_cases ?? [],
            design_codes: aiConfigOptions.design_codes ?? [],
            shells: aiConfigOptions.shells ?? [],
            endplates: aiConfigOptions.endplates ?? [],
            ears: aiConfigOptions.ears ?? [],
            flange_clamps: aiConfigOptions.flange_clamps ?? [],
            stud_bolts: aiConfigOptions.stud_bolts ?? [],
            seal_preferences: aiConfigOptions.seal_preferences ?? [],
            seal_types: aiConfigOptions.seal_types ?? [],
            seal_materials: aiConfigOptions.seal_materials ?? [],
            seal_sizes: aiConfigOptions.seal_sizes ?? [],
            strongbacks: aiConfigOptions.strongbacks ?? [],
            strongbacks_stud_bolts: aiConfigOptions.strongbacks_stud_bolts ?? [],
          }
        : {};
      const body = {
        mode: "mechanical",
        userInput,
        userId,
        useAssistant: true,
        chatId,
        configOptions: filteredConfigOptions,
      };
      console.log("Options sent to AI agent:", body);
      if (import.meta.env.DEV) {
        console.log("AI Agent Options Sent:", JSON.stringify(body, null, 2));
      }
      if (!import.meta.env.VITE_AI_AGENT_ENDPOINT) throw new Error("AI endpoint not configured");

      const initialToken = getAccessToken();
      if (!initialToken) throw new Error("No auth token available. Please login.");
      let currentToken = initialToken;

      const fetchAi = async (tok: string) => {
        return await fetch(import.meta.env.VITE_AI_AGENT_ENDPOINT, {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tok}`,
          },
          body: JSON.stringify(body),
        });
      };

      let res = await fetchAi(currentToken);

      if (res.status === 401) {
        await refreshAccessToken();
        const newToken = getAccessToken();
        if (!newToken) throw new Error("Failed to refresh token. Please login again.");
        currentToken = newToken;
        res = await fetchAi(currentToken);
      }

      if (!res.ok) {
        if (res.status === 401) {
          toast({ variant: "destructive", description: "Session expired. Please login again." });
          throw new Error("Unauthorized");
        }
        throw new Error("AI API failed");
      }

      return (await res.json()) as AiResponse;
    },
    retry: (failureCount, error) => failureCount < 3 && error.message !== "Unauthorized",
    onMutate: () => setIsAiLoading(true),
    onSuccess: (data) => {
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response || data.message || "Thinking..." },
      ]);
      if (data.chatId) setChatId(data.chatId);
      if (data.job) {
        toast({ description: "Job created by AI!" });
        navigate(`/jobs/${data.job.jobId}`);
        if (onClose) onClose();
        if (onOpenChange) onOpenChange(false);
      } else if (data.partialPayload) {
        setDefaultValues({ ...sections, ...data.partialPayload });
        toast({ description: "Form fields updated by AI." });
      }
    },
    onError: (error) => {
      toast({ variant: "destructive", description: `AI error: ${error.message}` });
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, an error occurred. Please try again." },
      ]);
    },
    onSettled: () => setIsAiLoading(false),
  });

  const handleSend = useCallback(() => {
    if ((!chatInput.trim() && pendingAttachments.length === 0) || isAiLoading || isConfigLoading) return;
    const attachmentText = pendingAttachments.length ? `\nAttached: ${pendingAttachments.join(", ")}` : "";
    const content = `${chatInput}${attachmentText}`;
    setChatMessages((prev) => [...prev, { role: "user", content, attachments: pendingAttachments }]);
    sendAiMessage(content);
    setChatInput("");
    setPendingAttachments([]);
    chatInputRef.current?.focus();
  }, [chatInput, isAiLoading, sendAiMessage, pendingAttachments, isConfigLoading]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map((f) => f.name);
      setPendingAttachments((prev) => [...prev, ...newFiles]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, isAiLoading]);

  useEffect(() => {
    if ((isOpen || isFullPage) && chatInputRef.current && !isAiLoading && !isConfigLoading) {
      chatInputRef.current.focus();
    }
  }, [isOpen, isFullPage, isAiLoading, isConfigLoading]);

  const sharedContent = (
    <>
      <div className="flex items-center gap-2 border-b pb-2">
        <InfoIcon className="h-5 w-5 text-primary-500" />
        <div>
          <Typography variant="text-md" weight="semibold">
            AI Assistant
          </Typography>
          <Typography variant="text-small" className="text-gray-500">
            Describe your job details, and I'll help fill the form or create it for you.
          </Typography>
        </div>
      </div>
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto flex flex-col gap-3 p-2">
        {chatMessages.length === 0 && (
          <Typography variant="text-small" className="text-center text-gray-500 mt-4">
            Start by describing your repair requirements.
          </Typography>
        )}
        {chatMessages.map((msg, index) => (
          <div
            key={index}
            className={cn(
              "p-2 rounded-lg max-w-[80%]",
              msg.role === "user" ? "bg-blue-100 self-end" : "bg-gray-100 self-start shadow-sm"
            )}
          >
            <Typography variant="text-small" weight="bold">
              {msg.role === "user" ? "You" : "AI"}
            </Typography>
            <div className="text-small prose prose-sm max-w-none break-words whitespace-pre-wrap leading-relaxed">
              <ReactMarkdown>{msg.content || ""}</ReactMarkdown>
            </div>
            {msg.attachments && msg.attachments.length > 0 && (
              <div className="mt-2">
                <Typography variant="text-small" className="font-semibold">
                  Attachments:
                </Typography>
                {msg.attachments.map((name, idx) => (
                  <Typography key={idx} variant="text-small">
                    {name}
                  </Typography>
                ))}
              </div>
            )}
          </div>
        ))}
        {isAiLoading && (
          <Typography variant="text-small" className="text-gray-500">
            AI is thinking...
          </Typography>
        )}
        {isConfigLoading && (
          <Typography variant="text-small" className="text-gray-500">
            Loading config options...
          </Typography>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-1">
          {pendingAttachments.map((name, idx) => (
            <div key={idx} className="bg-gray-200 px-2 py-1 rounded flex items-center gap-1">
              <Typography variant="text-small">{name}</Typography>
              <button
                onClick={() => setPendingAttachments((prev) => prev.filter((_, i) => i !== idx))}
                className="text-red-500"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isAiLoading || isConfigLoading}
            size="default"
          >
            Attach
          </Button>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            className="hidden"
            accept=".pdf,.gif,.tiff,.tif,.jpg,.jpeg,.png,.bmp,.webp,.html,.docx,.pptx,.xlsx"
            onChange={handleFileChange}
          />
          <TextInput
            ref={chatInputRef}
            placeholder="Type your message..."
            value={chatInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChatInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isAiLoading || isConfigLoading}
            className="flex-1"
          />
          <Button
            onClick={handleSend}
            disabled={(!chatInput.trim() && pendingAttachments.length === 0) || isAiLoading || isConfigLoading}
            size="default"
          >
            Send
          </Button>
        </div>
      </div>
    </>
  );

  const contentClassName = cn(
    "flex flex-col gap-4",
    isFullPage ? "h-[calc(100vh-200px)] overflow-hidden bg-white p-4 md:p-6" : "max-h-[80vh] sm:max-w-4xl"
  );

  if (isFullPage) {
    return <div className={contentClassName}>{sharedContent}</div>;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTitle className="hidden">Create Job with AI Assistant</DialogTitle>
      <DialogDescription className="hidden">Use AI to help create and configure your mechanical job</DialogDescription>
      <DialogContent className={contentClassName}>{sharedContent}</DialogContent>
    </Dialog>
  );
}
